import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { employeeApi, quickbooksApi, extractErrorMessage } from '../../../services/hrApi';
import type { Employee } from '../../../types';
import ConfirmationModal from '../../../components/ConfirmationModal';
import './StepStyles.scss';

interface QuickBooksInactivationStepProps {
  employeeId?: string;
  data?: any;
  onDataChange: (data: any) => void;
  onComplete: (isComplete: boolean) => void;
}

const QuickBooksInactivationStep: React.FC<QuickBooksInactivationStepProps> = ({
  employeeId,
  data,
  onDataChange,
  onComplete,
}) => {
  const [employee, setEmployee] = useState<Employee | null>(null);
  const [checkingStatus, setCheckingStatus] = useState(true);
  const [quickbooksConnected, setQuickbooksConnected] = useState(false);
  const [quickbooksLoading, setQuickbooksLoading] = useState(false);
  const [inactivating, setInactivating] = useState(false);
  const [inactivated, setInactivated] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  useEffect(() => {
    if (employeeId) {
      fetchEmployee();
    } else {
      setCheckingStatus(false);
    }
  }, [employeeId]);

  const checkQuickBooksStatus = useCallback(async () => {
    setQuickbooksLoading(true);
    try {
      const status = await quickbooksApi.checkStatus();
      setQuickbooksConnected((status.connected && status.valid) || false);
    } catch (error) {
      console.error('Error checking QuickBooks status:', error);
      setQuickbooksConnected(false);
    } finally {
      setQuickbooksLoading(false);
      setCheckingStatus(false);
    }
  }, []);

  useEffect(() => {
    checkQuickBooksStatus();
  }, [checkQuickBooksStatus]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data === 'quickbooks-auth-success') {
        checkQuickBooksStatus();
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [checkQuickBooksStatus]);

  useEffect(() => {
    if (employee) {
      // Check if employee is already inactivated (no quickbooks_employee_id means not in QuickBooks)
      // For offboarding, we consider it complete if:
      // 1. Employee has quickbooks_employee_id and we've inactivated them, OR
      // 2. Employee doesn't have quickbooks_employee_id (not in QuickBooks, so nothing to inactivate)
      const hasQuickBooksId = !!(employee.quickbooks_employee_id && employee.quickbooks_employee_id.trim() !== '');
      const isInactivated = inactivated || !hasQuickBooksId;
      
      // Always call onComplete to ensure step completion status is updated
      // This is especially important when employee is not in QuickBooks (no action needed = step complete)
      onComplete(isInactivated);
    } else if (!employeeId) {
      // If no employeeId, step cannot be completed
      onComplete(false);
    }
  }, [employee, inactivated, onComplete, employeeId]);

  const fetchEmployee = async () => {
    if (!employeeId) return;
    try {
      const emp = await employeeApi.getEmployee(employeeId);
      setEmployee(emp);
      // If employee doesn't have quickbooks_employee_id, they're not in QuickBooks
      // So inactivation is not needed - mark as complete
      if (!emp.quickbooks_employee_id) {
        setInactivated(true);
      }
    } catch (error) {
      console.error('Error fetching employee:', error);
      toast.error('Failed to load employee data');
    }
  };

  const handleQuickBooksLogin = async () => {
    try {
      setQuickbooksLoading(true);
      const response = await quickbooksApi.connect();

      if (response.success && response.auth_url) {
        window.open(response.auth_url, '_blank', 'width=600,height=700');
      } else {
        toast.error('Failed to get QuickBooks authorization URL. Please try again.');
      }
    } catch (error) {
      console.error('Error initiating QuickBooks login:', error);
      const errorMessage = extractErrorMessage(error);
      toast.error(`Failed to initiate QuickBooks login: ${errorMessage}`);
    } finally {
      setQuickbooksLoading(false);
    }
  };

  const handleInactivateEmployee = async () => {
    setShowConfirmModal(false);
    
    if (!employeeId) {
      toast.error('Employee ID is required');
      return;
    }

    if (!quickbooksConnected) {
      toast.error('Please connect to QuickBooks first');
      return;
    }

    if (!employee?.quickbooks_employee_id) {
      toast.error('Employee is not in QuickBooks. Nothing to inactivate.');
      return;
    }

    setInactivating(true);
    try {
      const result = await quickbooksApi.inactivateEmployee(employeeId);
      
      if (result.success) {
        toast.success('Employee inactivated in QuickBooks successfully!');
        setInactivated(true);
        await fetchEmployee();
      } else {
        toast.error(`Failed to inactivate employee in QuickBooks: ${result.error || 'Unknown error'}`);
      }
    } catch (error: any) {
      console.error('Error inactivating employee in QuickBooks:', error);
      const errorMessage = extractErrorMessage(error);
      toast.error(`Failed to inactivate employee in QuickBooks: ${errorMessage}`);
    } finally {
      setInactivating(false);
    }
  };

  const getStatusConfig = (isInactivated: boolean, hasQuickBooksId: boolean) => {
    if (!hasQuickBooksId) {
      return {
        icon: 'bx bx-info-circle',
        iconBackground: '#e9ecef',
        iconShadow: 'rgba(108, 117, 125, 0.2)',
        borderColor: '#dee2e6',
        badgeBackground: '#6c757d',
        badgeColor: '#ffffff',
        description: 'Employee is not in QuickBooks. No action needed.',
        statusText: 'Not in QuickBooks'
      };
    }
    
    if (isInactivated) {
      return {
        icon: 'bx bx-check-circle',
        iconBackground: '#e8f5e9',
        iconShadow: 'rgba(40, 167, 69, 0.2)',
        borderColor: '#c3e6cb',
        badgeBackground: '#28a745',
        badgeColor: '#ffffff',
        description: 'Employee has been successfully inactivated in QuickBooks.',
        statusText: 'Inactivated'
      };
    } else {
      return {
        icon: 'bx bx-file-blank',
        iconBackground: '#e9ecef',
        iconShadow: 'rgba(108, 117, 125, 0.2)',
        borderColor: '#dee2e6',
        badgeBackground: '#6c757d',
        badgeColor: '#ffffff',
        description: 'Employee has not been inactivated in QuickBooks yet.',
        statusText: 'Not Inactivated'
      };
    }
  };

  if (!employeeId) {
    return (
      <div className="wizard-step">
        <div className="step-header">
          <h4>QuickBooks Inactivation</h4>
          <p>Deactivate employee in QuickBooks</p>
        </div>
        <div className="step-info-box">
          <i className="bx bx-info-circle"></i>
          <p>Please complete the previous steps first.</p>
        </div>
      </div>
    );
  }

  const hasQuickBooksId = !!(employee?.quickbooks_employee_id && employee.quickbooks_employee_id.trim() !== '');
  const statusConfig = getStatusConfig(inactivated, hasQuickBooksId);

  return (
    <div className="wizard-step">
      <div className="step-header">
        <h4>QuickBooks Inactivation</h4>
        <p>Deactivate employee in QuickBooks</p>
        <div className="docusign-status-header">
          {quickbooksLoading ? (
            <span className="docusign-status-loading">
              <i className="bx bx-loader-alt bx-spin"></i>
              Checking QuickBooks...
            </span>
          ) : quickbooksConnected ? (
            <span className="docusign-status-connected">
              <i className="bx bx-check-circle"></i>
              QuickBooks: Logged in
            </span>
          ) : (
            <span className="docusign-status-disconnected">
              <i className="bx bx-x-circle"></i>
              QuickBooks: Not logged in
            </span>
          )}
        </div>
      </div>

      <div className="step-form">
        {checkingStatus && (
          <div style={{ 
            marginBottom: '20px', 
            textAlign: 'center', 
            padding: '60px 40px',
            background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
            borderRadius: '12px',
            border: '1px solid #e9ecef'
          }}>
            <div style={{
              width: '64px',
              height: '64px',
              margin: '0 auto 20px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #0dcaf0 0%, #0aa2c0 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 12px rgba(13, 202, 240, 0.3)'
            }}>
              <i className="bx bx-loader-alt bx-spin" style={{ fontSize: '32px', color: '#ffffff' }}></i>
            </div>
            <p style={{ margin: 0, fontSize: '16px', color: '#495057', fontWeight: 500 }}>
              Checking QuickBooks status...
            </p>
          </div>
        )}

        {!checkingStatus && (
          <div style={{ marginBottom: '20px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '32px 0' }}>
            <div
              style={{
                width: '96px',
                height: '96px',
                borderRadius: '50%',
                background: statusConfig.iconBackground,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '24px',
                boxShadow: `0 8px 24px ${statusConfig.iconShadow}`
              }}
            >
              <i 
                className={statusConfig.icon} 
                style={{ 
                  fontSize: '48px', 
                  color: statusConfig.badgeBackground
                }}
              ></i>
            </div>
            
            <h3 style={{
              margin: '0 0 12px 0',
              fontSize: '20px',
              fontWeight: 600,
              color: statusConfig.badgeBackground,
            }}>
              {statusConfig.statusText}
            </h3>
            
            <p style={{
              margin: 0,
              fontSize: '14px',
              color: '#6c757d',
              lineHeight: '1.6',
              maxWidth: '400px',
              textAlign: 'center'
            }}>
              {statusConfig.description}
            </p>

            {hasQuickBooksId && employee?.quickbooks_employee_id && (
              <p style={{
                margin: '16px 0 0 0',
                fontSize: '12px',
                color: '#868e96',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px'
              }}>
                <i className="bx bx-id-card" style={{ fontSize: '14px' }}></i>
                QuickBooks ID: {employee.quickbooks_employee_id}
              </p>
            )}

            {!inactivated && hasQuickBooksId && (
              <div style={{
                marginTop: '32px',
                display: 'flex',
                gap: '12px',
                justifyContent: 'center',
                flexWrap: 'wrap'
              }}>
                <button
                  type="button"
                  onClick={() => setShowConfirmModal(true)}
                  disabled={inactivating || !quickbooksConnected || quickbooksLoading}
                  style={{
                    padding: '10px 20px',
                    background: quickbooksConnected ? '#dc3545' : '#6c757d',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: 500,
                    cursor: (inactivating || !quickbooksConnected || quickbooksLoading) ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    transition: 'all 0.2s ease',
                    opacity: (inactivating || !quickbooksConnected || quickbooksLoading) ? 0.6 : 1
                  }}
                >
                  {inactivating ? (
                    <>
                      <i className="bx bx-loader-alt bx-spin"></i>
                      Inactivating...
                    </>
                  ) : (
                    <>
                      <i className="bx bx-x-circle"></i>
                      Inactivate in QuickBooks
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        )}

        {!quickbooksLoading && !quickbooksConnected && (
          <div className="docusign-lock-overlay">
            <div className="docusign-lock-content">
              <i className="bx bx-lock-alt" style={{ fontSize: '48px', color: '#6c757d', marginBottom: '16px' }}></i>
              <h4>Login Required</h4>
              <p>You have to login to QuickBooks in order to inactivate employees</p>
              <button
                type="button"
                className="btn-docusign-login-overlay"
                onClick={handleQuickBooksLogin}
                disabled={quickbooksLoading}
              >
                <i className="bx bx-log-in"></i>
                Login to QuickBooks
              </button>
            </div>
          </div>
        )}
      </div>

      <ConfirmationModal
        isOpen={showConfirmModal}
        title="Inactivate Employee in QuickBooks"
        message={`Are you sure you want to inactivate ${employee?.first_name} ${employee?.last_name} in QuickBooks? This will set their status to inactive.`}
        confirmText="Inactivate"
        cancelText="Cancel"
        variant="warning"
        icon="bx bx-x-circle"
        onConfirm={handleInactivateEmployee}
        onCancel={() => setShowConfirmModal(false)}
        isLoading={inactivating}
      />
    </div>
  );
};

export default QuickBooksInactivationStep;

